import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import {
  useMetricsUi,
  PillTabs,
  PAGE_FONT,
  MONO_FONT,
} from "../../components/metrics/metricsUi";
import { inventoryAnalysisService } from "../../utils/services/inventoryAnalysisService";
import { isMobile } from "../../globalStyles/mobile";

const TAB_BY_NODE = "byNode";
const TAB_ALL = "all";
const TAB_ATTENTION = "attention";
const TAB_QUEUE = "queue";

const isKnown = (value) =>
  value != null && value !== "" && Number.isFinite(Number(value));

const fmtUnits = (n) =>
  isKnown(n) ? `${Math.round(Number(n)).toLocaleString("en-US")} u` : "—";

const fmtCurrency = (n) => {
  if (!isKnown(n)) return "—";
  const value = Number(n);
  const abs = Math.abs(Math.round(value)).toLocaleString("en-US");
  return `${value < 0 ? "-" : ""}$${abs}`;
};

const fmtDays = (n) => (isKnown(n) ? `${Number(n).toFixed(1)}d` : "—");

const InventoryAnalysisPage = () => {
  const ui = useMetricsUi();
  const { colors, darkMode } = ui;
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const clientId = selectedClientData?._id || storedClientId;

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(storedClientId));
  const [error, setError] = useState("");
  const [tab, setTab] = useState(TAB_BY_NODE);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [nodeFilter, setNodeFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [openNodes, setOpenNodes] = useState(() => new Set());
  const requestSeq = useRef(0);
  const mobile = isMobile();

  // Debounce search so we don't hammer the API on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    try {
      const data = await inventoryAnalysisService.fetch({
        clientId,
        tab,
        search: search || undefined,
        node: nodeFilter || undefined,
        vendor: vendorFilter || undefined,
        abcClass: classFilter || undefined,
      });
      if (seq !== requestSeq.current) return;
      setPayload(data);
      const firstNode = data?.nodes?.[0]?.id;
      setOpenNodes((prev) => {
        if (prev.size && data?.nodes?.some((n) => prev.has(n.id))) return prev;
        return firstNode ? new Set([firstNode]) : new Set();
      });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setPayload(null);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load inventory analysis.",
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [clientId, tab, search, nodeFilter, vendorFilter, classFilter]);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      setPayload(null);
      setError("");
      return undefined;
    }
    fetchData();
    return undefined;
  }, [clientId, fetchData]);

  const rows = useMemo(() => payload?.rows || [], [payload]);
  const kpis = payload?.kpis;
  const filters = payload?.filters || {};
  const meta = payload?.meta || {};

  useEffect(() => {
    if (tab === TAB_QUEUE && payload && !meta.reconciliation_available) {
      setTab(TAB_BY_NODE);
    }
  }, [tab, payload, meta.reconciliation_available]);

  // Server already applied tab/search/filters — render response rows directly.
  const displayRows = rows;
  const tabCounts = {
    [TAB_BY_NODE]: meta.tab_counts?.byNode ?? meta.candidate_rows ?? 0,
    [TAB_ALL]: meta.tab_counts?.all ?? meta.candidate_rows ?? 0,
    [TAB_ATTENTION]:
      meta.tab_counts?.attention ?? kpis?.needs_attention_count ?? 0,
    [TAB_QUEUE]: meta.reconciliation_available
      ? (meta.tab_counts?.queue ?? kpis?.cycle_count_queue_count ?? 0)
      : null,
  };

  const selectSx = {
    fontSize: 13,
    backgroundColor: colors.bgPaper,
    borderRadius: "8px",
    minWidth: 120,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: ui.borderStrong,
    },
  };

  const toneColor = (tone) => {
    if (tone === "danger") return ui.danger;
    if (tone === "warning") return ui.warning;
    if (tone === "accent") return ui.accent;
    if (tone === "positive") return ui.positive;
    return colors.textPrimary;
  };

  const coverageColor = (row) => {
    if (!isKnown(row.days_coverage)) return colors.textTertiary;
    if (row.days_coverage < row.lead_time_days) return ui.danger;
    if (row.days_coverage < row.lead_time_days * 1.3) return ui.warning;
    return colors.textPrimary;
  };

  const QtyCell = ({ units, value, emphasize }) => (
    <Box sx={{ textAlign: "right" }}>
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 500,
          fontVariantNumeric: "tabular-nums",
          color: emphasize || colors.textPrimary,
          lineHeight: 1.3,
        }}
      >
        {fmtUnits(units)}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: emphasize || colors.textTertiary,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.3,
          mt: "1px",
        }}
      >
        {fmtCurrency(value)}
      </Typography>
    </Box>
  );

  const renderTable = (tableRows) => {
    if (!tableRows.length) {
      return (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: colors.textTertiary }}>
            No SKUs match the current filters.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            borderCollapse: "collapse",
            width: "100%",
            minWidth: 1500,
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {[
                "",
                "SKU",
                "Velocity",
                "Lead time",
                "Reorder pt",
                "On-hand",
                "ATS",
                "Days cov.",
                "Committed",
                "Quarantined",
                "Expected on-hand",
                "Unaccounted",
                "Recommended action",
              ].map((label, idx) => (
                <Box
                  component="th"
                  key={`${label}-${idx}`}
                  sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: colors.bgPaper,
                    textAlign: idx <= 1 ? "left" : idx === 12 ? "left" : "right",
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textTertiary,
                    px: 1.25,
                    py: 1.1,
                    borderBottom: `1px solid ${colors.border}`,
                    whiteSpace: "nowrap",
                    width: idx === 0 ? 3 : undefined,
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {tableRows.map((row) => {
              const tone = toneColor(row.action_tone);
              const unaccountedColor =
                row.unaccounted < 0
                  ? ui.danger
                  : row.unaccounted > 0
                    ? ui.warning
                    : colors.textPrimary;
              return (
                <Box
                  component="tr"
                  key={
                    row.position_id ||
                    `${row.sku}-${row.node_id}-${row.provider_id || "none"}`
                  }
                  sx={{
                    "&:hover td": {
                      backgroundColor: ui.surfaceAlt,
                    },
                  }}
                >
                  <Box
                    component="td"
                    sx={{
                      width: 3,
                      p: 0,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        minHeight: 52,
                        height: "100%",
                        backgroundColor: tone,
                      }}
                    />
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                      position: "sticky",
                      left: 0,
                      backgroundColor: colors.bgPaper,
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: MONO_FONT,
                        fontWeight: 600,
                        fontSize: 12,
                        color: colors.textPrimary,
                      }}
                    >
                      {row.sku}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: colors.textTertiary,
                        mt: "1px",
                      }}
                    >
                      {[
                        tab !== TAB_BY_NODE ? row.node_name : null,
                        row.provider_name
                          ? `Provider: ${row.provider_name}`
                          : null,
                        row.vendor || row.product_name || null,
                        row.abc_class ? `Class ${row.abc_class}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 12.5,
                      verticalAlign: "top",
                    }}
                  >
                    <Box>
                      {isKnown(row.velocity) ? `${row.velocity}/day` : "—"}
                      {row.velocity_status === "known_zero" && (
                        <Typography
                          sx={{ fontSize: 10.5, color: colors.textTertiary }}
                        >
                          No recent demand
                        </Typography>
                      )}
                      {row.velocity_is_stale && isKnown(row.velocity) && (
                        <Typography
                          sx={{ fontSize: 10.5, color: ui.warning }}
                        >
                          Stale window
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 12.5,
                      verticalAlign: "top",
                    }}
                  >
                    {row.lead_time_days}d
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 12.5,
                      verticalAlign: "top",
                    }}
                  >
                    {fmtUnits(row.reorder_point)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                    }}
                  >
                    <QtyCell
                      units={row.on_hand}
                      value={row.on_hand_value}
                    />
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                    }}
                  >
                    <QtyCell units={row.ats} value={row.ats_value} />
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: coverageColor(row),
                      verticalAlign: "top",
                    }}
                  >
                    {fmtDays(row.days_coverage)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                    }}
                  >
                    <QtyCell
                      units={row.committed}
                      value={row.committed_value}
                    />
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                    }}
                  >
                    <QtyCell
                      units={row.quarantined}
                      value={row.quarantined_value}
                    />
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                    }}
                  >
                    <QtyCell
                      units={row.expected_on_hand}
                      value={row.expected_on_hand_value}
                    />
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                    }}
                  >
                    <QtyCell
                      units={row.unaccounted}
                      value={row.unaccounted_value}
                      emphasize={unaccountedColor}
                    />
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.25,
                      borderBottom: `1px solid ${colors.border}`,
                      verticalAlign: "top",
                      minWidth: 230,
                      maxWidth: 280,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.9,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          backgroundColor: tone,
                          mt: "5px",
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: tone,
                            lineHeight: 1.3,
                          }}
                        >
                          {row.action_label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: colors.textSecondary,
                            lineHeight: 1.4,
                            mt: "2px",
                          }}
                        >
                          {row.action_detail}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderByNode = () => {
    const byNode = new Map();
    displayRows.forEach((row) => {
      if (!byNode.has(row.node_id)) byNode.set(row.node_id, []);
      byNode.get(row.node_id).push(row);
    });
    const nodeOrder = (payload?.nodes || []).filter((n) => byNode.has(n.id));
    if (!nodeOrder.length) {
      return (
        <Box sx={{ ...ui.card, py: 5, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: colors.textTertiary }}>
            No priority SKUs match the current filters.
          </Typography>
        </Box>
      );
    }

    return nodeOrder.map((node) => {
      const nodeRows = [...byNode.get(node.id)].sort(
        (a, b) =>
          Math.abs(Number(b.unaccounted_value) || 0) -
          Math.abs(Number(a.unaccounted_value) || 0),
      );
      const open = openNodes.has(node.id);
      const knownNodeRows = nodeRows.filter((row) =>
        isKnown(row.unaccounted_value),
      );
      const unaccountedSum =
        node.unaccounted_value != null
          ? Number(node.unaccounted_value)
          : knownNodeRows.length
            ? knownNodeRows.reduce(
                (sum, row) => sum + Number(row.unaccounted_value),
                0,
              )
            : null;
      const flagged =
        node.flagged_count != null
          ? node.flagged_count
          : nodeRows.filter((r) => r.action_tone !== "positive").length;

      return (
        <Box key={node.id} sx={{ ...ui.card, mb: 1.5, overflow: "hidden" }}>
          <Box
            role="button"
            tabIndex={0}
            aria-expanded={open}
            onClick={() => {
              setOpenNodes((prev) => {
                const next = new Set(prev);
                if (next.has(node.id)) next.delete(node.id);
                else next.add(node.id);
                return next;
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
              cursor: "pointer",
              backgroundColor: ui.surfaceAlt,
              userSelect: "none",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <KeyboardArrowRightIcon
                sx={{
                  fontSize: 18,
                  color: colors.textSecondary,
                  transform: open ? "rotate(90deg)" : "none",
                  transition: "transform .15s ease",
                }}
              />
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, m: 0 }}>
                  {node.name}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: colors.textSecondary, m: 0 }}
                >
                  {[
                    node.operator ? `3PL: ${node.operator}` : null,
                    `${node.displayed_sku_count ?? nodeRows.length} priority positions shown`,
                    node.total_sku_count != null
                      ? `${node.total_sku_count.toLocaleString("en-US")} total`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: unaccountedSum < 0 ? ui.danger : colors.textPrimary,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {isKnown(unaccountedSum)
                  ? `${fmtCurrency(unaccountedSum)} unaccounted`
                  : "Reconciliation unavailable"}
              </Typography>
              <Typography
                sx={{ fontSize: 11.5, color: colors.textTertiary, mt: "2px" }}
              >
                {flagged} attention positions
              </Typography>
            </Box>
          </Box>
          {open && <Box>{renderTable(nodeRows)}</Box>}
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8",
        fontFamily: PAGE_FONT,
      }}
    >
      {loading && (
        <LinearProgress
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            height: 3,
          }}
        />
      )}

      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: mobile ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        <AnalysisPageTopbar
          badge="IA"
          title="Inventory Analysis"
          clientName={
            selectedClientData?.business_name || selectedClientData?.name
          }
          right={
            <>
              {!storedClientId && (
                <ClientAutocomplete
                  width={220}
                  selectedClient={selectedClientData}
                  onClientChange={setSelectedClientData}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: 12.5,
                  color: colors.textSecondary,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: ui.positive,
                    boxShadow: `0 0 0 3px ${ui.positiveBg}`,
                  }}
                />
                <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>
                  Inventory synced just now
                  {payload
                    ? ` · Showing ${displayRows.length} priority positions${
                        kpis?.position_count
                          ? ` of ${Number(kpis.position_count).toLocaleString("en-US")} eligible inventory positions`
                          : ""
                      }${
                        meta.velocity_window_end
                          ? ` · Demand through ${new Date(
                              `${meta.velocity_window_end}T00:00:00`,
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}${meta.velocity_is_stale ? " (stale)" : ""}`
                          : " · Demand unavailable"
                      }`
                    : ""}
                </Typography>
              </Box>
            </>
          }
        />

        {!clientId && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            Select a client to load inventory analysis.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}

        {loading && !payload && (
          <SalesPerformancePageLoading
            title="Loading inventory analysis..."
            kpiCount={4}
          />
        )}

        {clientId && !loading && payload && !error && kpis?.sku_count === 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            No inventory records found for this client.
          </Alert>
        )}

        {clientId &&
          !loading &&
          payload &&
          !error &&
          kpis?.sku_count > 0 &&
          displayRows.length === 0 && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
              No priority SKUs match the current search or filters.
            </Alert>
          )}

        {kpis && kpis.sku_count > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: mobile
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)",
              gap: 1.5,
              mb: 2.75,
            }}
          >
            {[
              {
                label: "Available to sell (priced)",
                value: fmtCurrency(kpis.available_to_sell_value),
                sub: kpis.priced_ats_position_count
                  ? `Client population · ${Number(
                      kpis.priced_ats_position_count,
                    ).toLocaleString("en-US")} positions with provider ATS and price`
                  : "No positions have both provider ATS and price",
                danger: false,
              },
              {
                label: "Net unaccounted (value)",
                value: fmtCurrency(kpis.net_unaccounted_value),
                sub: meta.reconciliation_available
                  ? "Trustworthy priority-position reconciliation"
                  : "Reconciliation unavailable",
                danger: kpis.net_unaccounted_value < 0,
              },
              {
                label: "Needs attention",
                value: `${kpis.needs_attention_count} positions`,
                sub: "From priority candidate analysis",
                danger: false,
              },
              {
                label: "Cycle count queue",
                value:
                  kpis.cycle_count_queue_count == null
                    ? "—"
                    : `${kpis.cycle_count_queue_count} positions`,
                sub: meta.reconciliation_available
                  ? "From trustworthy priority reconciliation"
                  : "Reconciliation unavailable",
                danger: false,
              },
            ].map((card) => (
              <Box
                key={card.label}
                sx={{
                  ...ui.card,
                  borderRadius: "10px",
                  p: "14px 16px",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, color: colors.textSecondary, mb: 0.75 }}
                >
                  {card.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    fontVariantNumeric: "tabular-nums",
                    color: card.danger ? ui.danger : colors.textPrimary,
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  sx={{ fontSize: 11.5, color: colors.textTertiary, mt: 0.5 }}
                >
                  {card.sub}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {payload && kpis?.sku_count > 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
                mb: 1.75,
              }}
            >
              <TextField
                size="small"
                placeholder="Search SKU or vendor"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 220,
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                    backgroundColor: colors.bgPaper,
                    borderRadius: "8px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: ui.borderStrong,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 16, color: colors.textTertiary }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small">
                <Select
                  displayEmpty
                  value={nodeFilter}
                  onChange={(e) => setNodeFilter(e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">All nodes</MenuItem>
                  {(filters.nodes || []).map((n) => (
                    <MenuItem key={n.id} value={n.id}>
                      {n.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small">
                <Select
                  displayEmpty
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">All vendors</MenuItem>
                  {(filters.vendors || []).map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small">
                <Select
                  displayEmpty
                  value={classFilter}
                  onChange={(e) => setClassFilter(e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">All classes</MenuItem>
                  {(filters.classes || []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 2 }}>
              <PillTabs
                value={tab}
                onChange={setTab}
                tabs={[
                  {
                    value: TAB_BY_NODE,
                    label: "By node",
                    badge: tabCounts[TAB_BY_NODE],
                  },
                  {
                    value: TAB_ALL,
                    label: "All positions",
                    badge: tabCounts[TAB_ALL],
                  },
                  {
                    value: TAB_ATTENTION,
                    label: "Needs attention",
                    badge: tabCounts[TAB_ATTENTION],
                  },
                  {
                    value: TAB_QUEUE,
                    label: "Cycle count queue",
                    badge: tabCounts[TAB_QUEUE],
                    disabled: !meta.reconciliation_available,
                    tooltip: !meta.reconciliation_available
                      ? "Reliable reconciliation data is unavailable for this view."
                      : undefined,
                  },
                ]}
              />
            </Box>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.textTertiary,
                mb: 1.5,
              }}
            >
              Priority inventory view — one row is one real SKU at one
              warehouse. Search to retrieve specific inventory positions outside
              this set.
            </Typography>

            {meta.matching_rows > displayRows.length && (
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: colors.textSecondary,
                  mb: 1.5,
                }}
              >
                Showing {displayRows.length} of {meta.matching_rows} matching
                priority positions.
              </Typography>
            )}

            {tab === TAB_BY_NODE ? (
              renderByNode()
            ) : (
              <Box sx={{ ...ui.card, overflow: "hidden" }}>
                {renderTable(displayRows)}
              </Box>
            )}

            <Box
              sx={{
                mt: 3.25,
                pt: 2,
                borderTop: `1px solid ${colors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: colors.textTertiary,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  mb: 1.25,
                }}
              >
                Calculation reference
              </Typography>
              {(payload.calculation_reference || []).map((item) => (
                <Typography
                  key={item.name}
                  sx={{
                    fontSize: 12,
                    color: colors.textSecondary,
                    lineHeight: 1.6,
                    mb: 0.85,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: colors.textPrimary, fontWeight: 600 }}
                  >
                    {item.name}
                  </Box>
                  {" — "}
                  <Box component="span" sx={{ fontFamily: MONO_FONT }}>
                    {item.formula}
                  </Box>
                  {item.hint ? (
                    <Box component="span" sx={{ color: colors.textTertiary }}>
                      {" "}
                      — {item.hint}
                    </Box>
                  ) : null}
                </Typography>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default InventoryAnalysisPage;
